import { parseAmountInput, toDecimal, toNumber } from '@/lib/decimal'
import type { LineType } from '@/lib/invoice-types'
import type { DistributionShareInput, RecurringDetail, RecurringGroupInput } from '@/lib/recurring-types'
import { isCompleteShare, sharePercent, toCents } from '@/lib/shares'

export interface ShareDraft {
  key: string
  beneficiary: string
  percentage: string
}

export interface LineDraft {
  key: string
  description: string
  quantity: string
  unitPrice: string
  amount: string
  lineType: LineType
  personId: string | null
  /** null betekent: volgt de verdeling van de groep. */
  ownDistribution: ShareDraft[] | null
}

export interface GroupDraft {
  key: string
  heading: string
  distribution: ShareDraft[]
  lines: LineDraft[]
}

let counter = 0
export function nextKey(): string {
  counter += 1
  return `k${counter}`
}

export function emptyShare(key: string): ShareDraft {
  return { key, beneficiary: '', percentage: '' }
}

export function emptyLine(): LineDraft {
  return { key: nextKey(), description: '', quantity: '', unitPrice: '', amount: '', lineType: 'FIXED', personId: null, ownDistribution: null }
}

export function emptyGroup(heading = 'Werkzaamheden'): GroupDraft {
  return { key: nextKey(), heading, distribution: [], lines: [emptyLine()] }
}

export function groupsFromDetail(detail: RecurringDetail): GroupDraft[] {
  if (detail.groups.length === 0) return [emptyGroup()]
  return detail.groups.map((group) => ({
    key: nextKey(),
    heading: group.heading,
    distribution: group.distribution.map((share) => ({ key: nextKey(), beneficiary: share.beneficiary, percentage: share.percentage })),
    lines: group.lines.map((line) => ({
      key: nextKey(),
      description: line.description,
      quantity: line.quantity ? String(toNumber(line.quantity)) : '',
      unitPrice: line.unitPrice ? toDecimal(toNumber(line.unitPrice)) : '',
      amount: toDecimal(toNumber(line.amount)),
      lineType: line.lineType,
      personId: line.personId,
      ownDistribution: line.distribution.length
        ? line.distribution.map((share) => ({ key: nextKey(), beneficiary: share.beneficiary, percentage: share.percentage }))
        : null,
    })),
  }))
}

export function parseAmount(value: string): string {
  return toDecimal(parseAmountInput(value) ?? 0)
}

function parseOptional(value: string): string | null {
  return value.trim() ? parseAmount(value) : null
}

export function toShareInput(shares: ShareDraft[]): DistributionShareInput[] {
  return shares
    .filter((share) => share.beneficiary !== '')
    .map((share) => ({ beneficiary: share.beneficiary, percentage: String(sharePercent(share.percentage)) }))
}

export function toGroupInputs(groups: GroupDraft[]): RecurringGroupInput[] {
  return groups
    .filter((group) => group.heading.trim() !== '')
    .map((group) => ({
      heading: group.heading.trim(),
      distribution: toShareInput(group.distribution),
      lines: group.lines
        .filter((line) => line.description.trim() !== '')
        .map((line) => ({
          description: line.description.trim(),
          quantity: parseOptional(line.quantity),
          unitPrice: parseOptional(line.unitPrice),
          amount: parseAmount(line.amount),
          lineType: line.lineType,
          personId: line.personId,
          distribution: line.ownDistribution ? toShareInput(line.ownDistribution) : [],
        })),
    }))
}

export function groupSubtotal(group: GroupDraft): number {
  return group.lines
    .filter((line) => line.description.trim() !== '')
    .reduce((sum, line) => sum + toCents(line.amount), 0)
}

export function draftSubtotal(groups: GroupDraft[]): number {
  return draftLines(groups).reduce((sum, { line }) => sum + toCents(line.amount), 0)
}

/** Aandelen zonder gekozen ontvanger tellen nergens mee; toShareInput laat ze ook vallen. */
export function filledShares(shares: ShareDraft[]): ShareDraft[] {
  return shares.filter((share) => share.beneficiary !== '')
}

/** De aandelen die voor een regel gelden: de eigen verdeling, anders die van de groep. */
export function sharesFor(group: GroupDraft, line: LineDraft): ShareDraft[] {
  const own = filledShares(line.ownDistribution ?? [])
  if (own.length > 0) return own
  return filledShares(group.distribution)
}

export interface DraftLine {
  group: GroupDraft
  line: LineDraft
}

/** Alle regels die straks in de PATCH belanden: gevulde kop, gevulde omschrijving. */
export function draftLines(groups: GroupDraft[]): DraftLine[] {
  return groups
    .filter((group) => group.heading.trim() !== '')
    .flatMap((group) => group.lines.filter((line) => line.description.trim() !== '').map((line) => ({ group, line })))
}

export type ProblemScope = 'content' | 'distribution'

export interface DraftProblem {
  scope: ProblemScope
  message: string
}

/**
 * Dezelfde eisen als RecurringUpsertService::replaceShares, zodat een reeks die
 * hier goedgekeurd wordt niet alsnog op een 422 van de server stuit. Een rij
 * zonder gekozen ontvanger is er nog geen: die telt hier net zo min mee als in
 * de payload.
 */
function shareProblems(shares: ShareDraft[], label: string): DraftProblem[] {
  if (shares.length === 0) return []

  const problems: DraftProblem[] = []
  if (shares.some((share) => sharePercent(share.percentage) < 0 || sharePercent(share.percentage) > 100)) {
    problems.push({ scope: 'distribution', message: `De verdeling van ${label} heeft een percentage buiten 0 en 100.` })
  }
  const seen = new Set<string>()
  for (const share of shares) {
    if (seen.has(share.beneficiary)) {
      problems.push({ scope: 'distribution', message: `In de verdeling van ${label} komt een ontvanger meer dan een keer voor.` })
      break
    }
    seen.add(share.beneficiary)
  }
  if (!isCompleteShare(shares)) {
    problems.push({ scope: 'distribution', message: `De verdeling van ${label} telt niet op tot 100%.` })
  }

  return problems
}

/**
 * De reeks is pas geldig als elke groep een kop en minstens een regel heeft, en
 * elke ingevulde verdeling exact op 100% sluit. Staat er ergens een verdeling,
 * dan moet elke regel gedekt zijn: half ingevuld accepteert de backend niet.
 *
 * Anders dan een enkele melding levert dit alle problemen op, met het tabblad
 * waar ze thuishoren, zodat de gebruiker niet naar een uitgeschakelde
 * opslaan-knop staart zonder te zien waar het aan ligt.
 */
export function groupsProblems(groups: GroupDraft[]): DraftProblem[] {
  const problems: DraftProblem[] = []
  const filled = groups.filter((group) => group.heading.trim() !== '')
  if (filled.length === 0) {
    return [{ scope: 'content', message: 'Voeg minstens een groep met een kop toe.' }]
  }

  const headings = new Set<string>()
  for (const group of filled) {
    const heading = group.heading.trim()
    if (headings.has(heading)) {
      problems.push({ scope: 'content', message: `De kop "${heading}" komt meer dan een keer voor.` })
    }
    headings.add(heading)
    if (!group.lines.some((line) => line.description.trim() !== '')) {
      problems.push({ scope: 'content', message: `Groep "${heading}" heeft nog geen regel.` })
    }
    problems.push(...shareProblems(filledShares(group.distribution), `groep "${heading}"`))
    for (const line of group.lines) {
      problems.push(
        ...shareProblems(filledShares(line.ownDistribution ?? []), `regel "${line.description || 'zonder omschrijving'}"`),
      )
    }
  }

  const anyDistribution = filled.some(
    (group) =>
      filledShares(group.distribution).length > 0 ||
      group.lines.some((line) => filledShares(line.ownDistribution ?? []).length > 0),
  )
  if (anyDistribution) {
    for (const { group, line } of draftLines(filled)) {
      if (sharesFor(group, line).length === 0) {
        problems.push({ scope: 'distribution', message: `Regel "${line.description}" heeft nog geen verdeling.` })
      }
    }
  }

  return problems
}

